/**
 * * Compare / Vergleichsliste
 *
 * Filename: 420_compare.js
 *
 * @category  Applicas
 * @package   Slider
 * @author    Shervin Mirostovar <s.mirostovar@fahrrad-xxl.de>
 *            Janik Kemnade <j.kemnade@fahrrad-xxl.de>
 * @copyright 2022 Fahrrad XXL
 */

import FxxlCore from '@fxxl/core';
import FxxlCompareProductVariantSelector from '@fxxl/compare_variant_selection';
import FxxlCompareModelInformationHandler from '@fxxl/compare_model_information_handler'
import FxxlPrint from '@fxxl/fxxl_print';

window.FXXL_COMPARE_REQUEST_ACTION_ADD_ITEM = 'addItem';
window.FXXL_COMPARE_REQUEST_ACTION_REMOVE_ITEM = 'removeItem';
window.FXXL_COMPARE_REQUEST_ACTION_UPDATE_ITEM = 'updateItem';

export class FxxlCompare {

    constructor(compareContainer) {
        // Configs & Predefinitions
        this.types = [ 'model', 'price', 'modelInformation', 'articlenumber', 'buybox' ];

        // Classnames & Ids
        this.compareItemClassName       = 'fxxl-artikel-comparison__item';
        this.elementLinkClassName       = 'fxxl-element-artikel__link';
        this.elementPriceFromClassName  = 'fxxl-element-artikel__price-from';

        // Variables
        this.container = compareContainer;
        this.products = null;
        this.compareItems = null;
        this.productVariantSelector = [];
        this.ModelInformationHandler = null;
        this.showingModelImage = true;

        // Init
        this.setupProductCompareList();
        this.compareItems = this.container.querySelectorAll(`.${this.compareItemClassName}[data-variant]`);
        this.products = this.setupProductsObject();
        this.initProductsVariantSelect();
        this.initModelInformationHandler()
    }

    /*
     * Private Methods
     * ------------------------------------------------------------------------
     */

    setupProductsObject() {
        /*
         * Product Object-Structure:
         * ----------------------------
         *   {
         *       12345: {
         *           productId: 12345,
         *           variantIds: [ 12345 ],
         *           items: [],
         *           imageItems: [],
         *           priceItems: [],
         *           articleNumberItems: [],
         *           ...,
         *           variants: {
         *               12345: {
         *                   items: [],
         *                   imageItem: '',
         *                   priceItem: '',
         *                   articleNumberItem: '',
         *                   ...,
         *               }
         *           }
         *       },
         *       ...
         *   }
         */


        const products = {};

        // Create / Map: Item Structure
        this.compareItems.forEach((item) => {
            const dataVariant = item.dataset.variant;
            const dataType = item.dataset.type;
            const productId = dataVariant.split('-')[0];
            const variantId = dataVariant.split('-')[1];

            // Init: Product & ProductId        -> ( 12345: { productId: 12345, ... )
            products[productId] = products[productId] || {
                productId: productId,
                variantIds: [],
                variants: {},
                items: []
            };

            // Init & Set: VariantIds Array     -> ( variantIds: [ 12345 ] )
            if (!products[productId]['variantIds'].includes(variantId)) {
                products[productId]['variantIds'].push(variantId);
            }

            // Init: Variants & VariantId       -> ( variants: { 12345: { variantId: 12345, .... )
            products[productId]['variants'][variantId] = products[productId]['variants'][variantId] || {
                variantId: variantId
            };

            // Init & Set: Product Items        -> ( 12345: { items: [], .... )
            products[productId]['items'].push(item);

            // Init & Set: Variant Items        -> ( variants: { 12345: { items: [], .... )
            const tempVariantItems = products[productId]['variants'][variantId]['items'];
            products[productId]['variants'][variantId]['items'] = tempVariantItems || [];
            products[productId]['variants'][variantId]['items'].push(item);

            // Init & Set: Types
            this.types.forEach((type) => {
                const productTypeName = `${type}Items`;
                const variantTypeName = `${type}Item`;

                // Init: Product TypeItems
                products[productId][productTypeName] = products[productId][productTypeName] || [];

                // Init: Variant TypeItems
                const tempVariantTypeItem = products[productId]['variants'][variantId][variantTypeName];
                products[productId]['variants'][variantId][variantTypeName] = tempVariantTypeItem || [];

                // Init: Product TypeItems      -> ( 12345: { priceItems: [], ... )
                // Init: Variant TypeItems      -> ( variants: { 12345: { priceItems: [], .... )
                if (dataType === type) {
                    products[productId][productTypeName].push(item);
                    products[productId]['variants'][variantId][variantTypeName].push(item);
                }
            });
        });

        // Init & Set: Additional Values
        this.setupProductsObjectExtraElement(products, 'modelItem', 'modelLink', this.elementLinkClassName);
        this.setupProductsObjectExtraElement(products, 'priceItem', 'priceFrom', this.elementPriceFromClassName);
        this.setupProductsObjectModelLinkHref(products);

        return products;
    }

    setupProductsObjectExtraElement(products, baseVariantElement, extraElementName, extraElementClassName) {
        const extraElementSingularName = `${extraElementName}Element`;
        const extraElementPluralName = `${extraElementName}Elements`;

        for (const [productId, productObject] of Object.entries(products)) {
            for (const [variantId, variantObject] of Object.entries(productObject.variants)) {
                // Init: Product ExtraElements
                products[productId][extraElementPluralName] = products[productId][extraElementPluralName] || [];

                // Init: Variant ExtraElements
                const tempExtraElement = products[productId]['variants'][variantId][extraElementSingularName];
                products[productId]['variants'][variantId][extraElementSingularName] = tempExtraElement || [];

                // Set: ExtraElements
                if (variantObject?.[baseVariantElement] && variantObject[baseVariantElement]?.length !== 0) {
                    const extraElement = variantObject[baseVariantElement][0].querySelector(`.${extraElementClassName}`);

                    if (extraElement) {
                        products[productId][extraElementPluralName].push(extraElement);
                        products[productId]['variants'][variantId][extraElementSingularName].push(extraElement);
                    }
                }
            }
        }
    }

    setupProductsObjectModelLinkHref(products) {
        for (const [productId, productObject] of Object.entries(products)) {
            for (const [variantId, variantObject] of Object.entries(productObject.variants)) {
                // Inet ModelLink
                products[productId]['modelLink'] = products[productId]['modelLink'] || null;
                products[productId]['variants'][variantId]['modelLink'] = null;

                // Set: ModelLink
                if (variantObject?.['modelLinkElement'] && variantObject['modelLinkElement']?.[0]?.href) {
                    const modelLink = variantObject['modelLinkElement'][0]?.href;
                    products[productId]['modelLink'] = products[productId]['modelLink'] || modelLink.split('?')[0];
                    products[productId]['variants'][variantId]['modelLink'] = modelLink;
                }
            }
        }
    }

    initProductsVariantSelect() {
        for (const [productId, productObject] of Object.entries(this.products)) {
            this.productVariantSelector[productId] = new FxxlCompareProductVariantSelector(
                this.container,
                productObject
            );
        }
    }

    initModelInformationHandler() {
        this.ModelInformationHandler = new FxxlCompareModelInformationHandler(this.container);
    }

    setupProductCompareList() {
        const $container = $(this.container);
        let item;
        let row;
        let cartButton;
        let buttonVariantId;
        let letHeaderBeSticky = false;
        let containerGroup = this.container.hasAttribute('data-group') ? this.container.dataset.group : '',
            containerGroupProductsCount = fxxlCompareProductCountByGroup[containerGroup] ?? 1;

        fxxlHideGuuruChatBySelectModal(this.container);
        fxxlHideSupportWidgetBySelectModal(this.container);

        $container.find('.fxxl-artikel-comparison__item').each(function () {
            var item = $(this),
                row = item.data('row'),
                tpl_class = item.data('tpl_class');
            if (row % 2 === 0 && tpl_class !== 'fixed') {
                item.addClass('fxxl-artikel-comparison__item--gray');
            }

            if (row > 10) {
                if (item.hasClass('fxxl-artikel-comparison__item--footer') !== true) {
                    ///  item.addClass('fxxl-artikel-comparison__item--more');
                }
            }
        });

        if (fxxlCompareProductsCount !== undefined && fxxlCompareProductsCount > 1 && this.container.hasAttribute('data-group') === false) {
            $container.parent().addClass('fxxl-artikel-comparison--' + fxxlCompareProductsCount + '-item');
        } else if (containerGroup !== '' && containerGroupProductsCount > 1) {
            $container.parent().addClass('fxxl-artikel-comparison--' + containerGroupProductsCount + '-item');
        }

        new FxxlElementVersionControl('.fxxl-artikel-comparison__item--footer .fxxl-button', largViewportSize, function () {
            $container.find('.fxxl-button').each(function () {
                cartButton = $(this);
                buttonVariantId = cartButton.data('variant-id');
                if (isLargeViewport() !== true) {
                    if (cartButton.find('svg').length === 0) {
                        cartButton.addClass('fxxl-button--textless');
                        cartButton.find('span').remove();
                        cartButton.append('<svg width="24" height="24">' +
                            '<use href="#sprite_icon_location_on" xlink:href="#sprite_icon_location_on"></use></svg>');
                    } else {
                        cartButton.addClass('fxxl-button--textless');
                        cartButton.find('span').remove();
                        cartButton.find('svg').removeAttr('class');
                    }
                }
            });
        });

        this.setupModelImageContainer($container);
        this.setupModelImageHolders($container);
        this.setCompareListEvents($container);
        this.setCompareListHeight($container);
        this.setupCompareShareLink();
        this.setupComparePrintLink();
        new FxxlResizedEvent(() => {
            this.setCompareListHeight($container);
            if (isLargeViewport() === true) {
                this.productCompareModelController($container, true);
            }
            this.setupModelImageHolders($container);
        });
    }

    setupModelImageContainer(list) {
        var imageContainer,
            imageHolder = $('<div class="fxxl-element-artikel__image-holder"></div>'),
            imageHolderTemp,
            image;

        list.find('.fxxl-element-artikel__image').each(function () {
            image = $(this);
            imageContainer = image.parent();

            imageHolderTemp = imageHolder.clone().append(image);
            imageContainer.prepend(imageHolderTemp);
        });
    }

    setupModelImageHolders(list) {
        var productImageHolders = list.find('.fxxl-element-artikel .fxxl-element-artikel__image-holder'),
            holder,
            imageHeight,
            image,
            __this = this;

        setTimeout(function () {
            productImageHolders.each(function () {
                holder = $(this);
                image = holder.find('img');
                imageHeight = image.height();

                if (imageHeight > 0 && __this.showingModelImage === true) {
                    holder.css({'padding-bottom': imageHeight + 'px', 'height': '0', 'overflow': 'hidden'});
                    image.attr('data-height', imageHeight);
                }
            });
        }, 100);

        setTimeout(function () {
            productImageHolders.addClass('fxxl-element-artikel__image-holder--activated');
        }, 150);
    }

    productCompareModelController(list, showImage) {
        var productImages = list.find('.fxxl-element-artikel .fxxl-element-artikel__image'),
            image,
            imageHolder;

        productImages.each(function () {
            image = $(this);
            imageHolder = image.parent();

            if (showImage === false) {
                imageHolder.css({'padding-bottom': '0', 'height': '0', 'overflow': 'hidden'});
                list.attr('data-model-extended', false);
                list.find('.fxxl-element-artikel__compare-button').css({'opacity': '0'});
                list.find('.fxxl-artikel-comparison__shadow').addClass('fxxl-artikel-comparison__shadow--show');
            } else {
                imageHolder.css({'padding-bottom': image.data('height') + 'px', 'height': '0', 'overflow': 'hidden'});
                list.attr('data-model-extended', true);
                list.find('.fxxl-element-artikel__compare-button').css({'opacity': '1'});
                list.find('.fxxl-artikel-comparison__shadow').removeClass('fxxl-artikel-comparison__shadow--show');
            }
        });
    }

    setCompareListHeight(element) {
        var windowHeight = $(window).height(),
            offsetTop = element.offset().top;

        if (isLargeViewport() !== true) {
            element.css({'max-height': (windowHeight - offsetTop + 60) + 'px'});
            //$('body').addClass('fxxl-fix-body');
        } else {
            element.css({'max-height': ''});
            //$('body').removeClass('fxxl-fix-body');
        }
    }

    showCompareModal(text, header) {
        var element = document.getElementById('fxxl-artikel-comparison-modal'),
            headetTextClass= 'ja-fxxl-modal__header-text-in';
        if (element !== undefined) {
            if (header === undefined) {
                header = element.getElementsByClassName(headetTextClass)[0].getAttribute('data-default-text');
            }
            element.getElementsByClassName(headetTextClass)[0].innerHTML = header;
            element.getElementsByClassName('modal-content')[0].innerHTML = text;
            M.Modal.getInstance(element).open();
        }
    }

    setCompareListEvents(list) {
        const __this = this;
        let lastScrollTop = 0;
        let titleLeft;
        let showHideModelEnable = true;
        let compareTitles = $('.fxxl-artikel-comparison__item--title');

        list[0].addEventListener('scroll', function (e) {
            let topPos = list[0].scrollTop;

            if (topPos > lastScrollTop){
                // downscroll code
                if (topPos - lastScrollTop > 15) {
                    showHideModelEnable = true;
                }
            } else {
                // upscroll code
                if (lastScrollTop - topPos > 50) {
                    showHideModelEnable = false;
                    __this.showingModelImage = true;
                    __this.productCompareModelController(list, true);
                }
            }

            if (topPos <= 0) {
                topPos = 0;
            }

            lastScrollTop = topPos;

            titleLeft = list[0].scrollLeft;
            titleLeft -= 10;
            compareTitles.css({'transform': 'translate3d(' + titleLeft + 'px, 0, 0)', 'transition': 'transform 0.15s'});
            if (showHideModelEnable === true) {
                var scrollTop = list.scrollTop(),
                    minScroll = list.find('.fxxl-element-artikel').first().height();

                if (scrollTop > minScroll && list.attr('data-model-extended') !== 'false') {
                    __this.showingModelImage = false;
                    __this.productCompareModelController(list, false);
                } else if (scrollTop <= minScroll && list.attr('data-model-extended') === 'false') {
                    __this.showingModelImage = true;
                    __this.productCompareModelController(list, true);
                }
            }
        }, true);

        if (isLargeViewport() === false) {
            // Required for showing variant-select-popup into the screen
            let triggerNodes = document.querySelectorAll('.fxxl-element-artikel__branch-button, .fxxl-select__input, .fxxl-select__icon--closed'),
                eventType;

            [...triggerNodes].forEach(node => {
                eventType = node.classList.contains('fxxl-select__input') || node.classList.contains('fxxl-select__icon--closed') ? 'touchstart' : 'click';
                node.addEventListener(eventType, (e) => {
                    setTimeout(function() {
                        requestAnimationFrame(function () {
                            list[0].scrollTop = list[0].scrollHeight;
                            window.scrollTo({
                                top: list[0].scrollHeight - window.innerHeight - list[0].scrollTop,
                                left: 0,
                                behavior: 'smooth'
                            });
                        });
                    }, 250);
                });
            });
        }


        let tabs = document.querySelectorAll('.fxxl-artikel-comparison__tab-header');

        tabs.forEach(function(tab) {
            __this.setProductCountTitleInfo(tab);
            tab.addEventListener("click", (event) => {
                let tabId = tab.dataset.href;
                let accordions = document.getElementById(tabId).getElementsByClassName('fxxl-accordion');
                for (const [key, value] of Object.entries(accordions)) {
                    setTimeout(function () {
                        __this.setCompareListHeight(list);
                        value.fxxlAccordion.setContentSize();
                        __this.setProductCountTitleInfo(tab);
                        __this.setupModelImageHolders(list);
                    }, 250);
                }
            });

            // wenn es nur eine Kategorie gibt (bsw. Sonstige) soll diese immer ausgeklappt sein.
            let parentTab = document.getElementById(tab.dataset.href);
            if (parentTab) {
                let availableGroups = parentTab.querySelectorAll('.fxxl-accordion');
                if (availableGroups.length === 1 && availableGroups[0].dataset.extended === '0') {
                    availableGroups[0].querySelector('.fxxl-accordion__head').click();
                }
            }
        });
    }

    setProductCountTitleInfo(tab) {
        let tabId = tab.dataset.href;
        if (tab.classList.contains('active')) {
            let currentGroup = tabId.split('-')[1];
            const comparisonTitleInfo = document.querySelector('.fxxl-artikel-comparison__title-inf');

            try {
                if (FxxlPersonalData) {
                    let activeTabProducts = FxxlPersonalData?.productcomparelist?.itemsByProductGroup;
                    if (activeTabProducts && activeTabProducts.hasOwnProperty(currentGroup)) {
                        comparisonTitleInfo.textContent = Object.keys(activeTabProducts[currentGroup]).length;
                        comparisonTitleInfo.classList.remove('fxxl-hidden');
                    }
                }
            } catch (e) {
                console.warn(e);
            }
        }
    }

    setupCompareShareLink() {
        const shareLinkElement = $('.fxxl-artikel-comparison__link--share');
        let linkElement, link, linkTextElement;
        shareLinkElement.on('click', function () {
            linkElement = $(this);
            link = linkElement.attr('href');
            linkTextElement = linkElement.find('.fxxl-artikel-comparison__link-text');
            if (navigator.share !== undefined) {
                navigator.share({
                    title: document.title,
                    text: linkTextElement.text(),
                    url: link
                }).then().catch(function (error) {
                    console.log('Error sharing:', error);
                });
            } else {
                // fxxlShareLinkCopyDoneText wurde erstellt in:
                // /srv/www/applicas/dev/customer_fahrradxxl/custom/module/public/fxxl3/artikel/artikel_comparison.tpl
                fxxlCopyTextToClipboard(link, fxxlShareLinkCopyDoneText);
            }
            return false;
        });
    }

    setupComparePrintLink() {
        const printLink = this.container.querySelector('.fxxl-artikel-comparison__link--print');
        const printMainContent = document.querySelector('.fxxl-artikel-comparison');

        if (printLink && printMainContent) {
            printLink.addEventListener('click', () => {
                FxxlPrint.print(printMainContent);
            });
        }
    }


    /*
     * Static / Public Methods
     * ------------------------------------------------------------------------
     */
    static toggleItem(element) {
        this.sendRequest({
            id: element?.dataset?.productId,
            varId: element?.dataset?.variantId,
            preselectedColor: element?.dataset?.preselectedColor,
            action: (element.checked) ? FXXL_COMPARE_REQUEST_ACTION_ADD_ITEM : FXXL_COMPARE_REQUEST_ACTION_REMOVE_ITEM,
            toast: true
        }, (response) => {
            if (response === false) element.checked = false;
        });
    }

    static removeItem(element) {
        showSpinner();
        this.sendRequest({
            id: element?.dataset?.productId,
            varId: element?.dataset?.variantId,
            action: FXXL_COMPARE_REQUEST_ACTION_REMOVE_ITEM,
            toast: false
        }, () => {
            let productGroup = element?.dataset?.productGroup ?? '';
            if (FxxlPersonalData?.productcomparelist?.itemsByProductGroup?.hasOwnProperty(productGroup)) {
                window.location.hash = 'tab-' + productGroup;
            }
            location.reload();
        });
    }

    static sendRequest(param, callback = null) {
        const { id, varId, preselectedColor = '', action, toast } = param;

        if (id === undefined || varId === undefined || action === undefined ) {
            console.warn('Error: ', 'FxxlCompare.sendRequest()');
            return;
        }

        const requestBase = '/json.php';
        const requestParams = new URLSearchParams();
        let toastText = '';
        let onclickCompareProductType = false;

        // Adjust Variables based on Action
        switch (action) {
            case FXXL_COMPARE_REQUEST_ACTION_ADD_ITEM:
                requestParams.append('service', 'addItemToCompareList');
                requestParams.append('id', id);
                requestParams.append('varid', varId);
                requestParams.append('color', preselectedColor);
                toastText = fxxlCompareAddText;
                onclickCompareProductType = 'adding';
                break;
            case FXXL_COMPARE_REQUEST_ACTION_REMOVE_ITEM:
                requestParams.append('service', 'removeItemFromCompareList');
                requestParams.append('id', id);
                requestParams.append('varid', varId);
                toastText = fxxlCompareRemoveText;
                onclickCompareProductType = 'removing';
                break;
            case FXXL_COMPARE_REQUEST_ACTION_UPDATE_ITEM:
                requestParams.append('service', 'updateItemFromCompareList');
                requestParams.append('id', id);
                requestParams.append('varid', varId);
                break;
            default:
                console.warn('Error: ', 'FxxlCompare.sendRequest()');
                break;
        }

        // Fetch & Handle Request
        fetch(`${requestBase}?${requestParams.toString()}`)
            .then((response) => response.json())
            .then((response) => {
                FxxlCompare.requestHandleResponse(
                    response,
                    onclickCompareProductType,
                    toast,
                    toastText,
                    callback
                );
            })
            .catch((error) => console.warn('Error:', 'FxxlCompare.sendRequest', error) );
    }

    static requestHandleResponse(response, onclickCompareProductType, toast, toastText, callback) {
        const callBackArg = (response !== -1 && response !== 0) ? response : false;
        const toastHtml   = (response !== -1 && response !== 0) ? toastText : (response === 0 ? fxxlCompareEscapeText : fxxlCompareLimitText);
        const toastClass  = (response !== -1) ? 'fxxl-toast' : 'fxxl-toast fxxl-toast--error';
        if (response !== -1 && response !== 0) {
            if (FxxlPersonalData !== undefined) {
                FxxlPersonalData.productcomparelist = response;
            }
            fxxlSetInitialComparisonWidgetCounter(onclickCompareProductType);
        }

        if (toast !== false) {
            M.toast({ html: toastHtml, classes: toastClass, displayLength: 1000 });
        }

        typeof callback == "function" && callback(callBackArg);
    }

    static setConfigWishlistCompareButton() {
        FxxlCore.scheduleIdleTask(() => {
            const num = FxxlPersonalData?.productcomparelist?.numItems;
            const buttonContainer = $('.fxxl-merkliste__compare-button-container');

            if (num !== undefined) {
                if (num > 1) {
                    buttonContainer.show();
                } else {
                    buttonContainer.hide();
                }
            }
        });
    }

    static updateCompareShareLink(newLink) {
        const shareLinks = document.querySelectorAll('.fxxl-artikel-comparison__link--share');
        shareLinks.forEach(link => {
            link.href = `${window.location.origin}/produktvergleich?share=${newLink}`;
        });
    }

}